import React from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import {
  ARTICLE_CARD_TYPES,
  ARTICLE_TYPES,
  CARD_TYPES,
} from '../../../shared/constants';
import {
  IArticle,
  IContentfulLink,
  IParentPage,
  Tag,
} from '../../../types/shared';

import PlayIcon from '../../atoms/icons/PlayIcon';
import PodcastIcon from '../../atoms/icons/PodcastIcon';
import Link from '../../atoms/link/Link';
import TagList from '../../atoms/tag-list/TagList';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import elevations from '../../../styles/elevations';
import { H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  contentType?: string;
  tags?: Tag[];
  image?: IGatsbyImageData;
  title: string;
  data?: IArticle;
  primaryLink?: IContentfulLink;
  articleType: string;
  cardType?: string;
  imageHeight?: 'small' | 'large';
}

const ArticlePreviewCard = ({
  contentType,
  tags,
  image,
  title,
  data,
  primaryLink,
  articleType,
  cardType = ARTICLE_CARD_TYPES.splitImage,
  imageHeight = 'small',
}: Props) => {
  const link =
    articleType === ARTICLE_TYPES.external
      ? (primaryLink?.externalUrl as string)
      : `/${getInternalLink(
          data?.parentPage as IParentPage,
          data?.slug as string
        )}`;
  const isCardTypeFullImage = cardType === ARTICLE_CARD_TYPES.fullImage;

  const renderCardCopy = () => {
    return (
      <TextContainer positionAbsolute={isCardTypeFullImage}>
        {tags?.length ? (
          <StyledTagList tags={tags} backgroundFilled={isCardTypeFullImage} />
        ) : undefined}
        {title && (
          <Title
            data-testid="article-preview-title"
            colorWhite={isCardTypeFullImage}
          >
            {title}
          </Title>
        )}
      </TextContainer>
    );
  };

  return (
    <StyledLink
      href={link}
      data-testid={`${CARD_TYPES.articlePreviewCard} ${articleType}`}
      trackingComponent={CARD_TYPES.articlePreviewCard}
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={title}
    >
      {image && isCardTypeFullImage ? (
        <FullImageContainer>
          <StyledGatsbyImage
            image={image}
            alt={`${CARD_TYPES.articlePreviewCard}-full-image`}
          />
          {renderCardCopy()}
        </FullImageContainer>
      ) : (
        <>
          {image && (
            <ImageContainer imageHeight={imageHeight}>
              <StyledGatsbyImage
                image={image}
                alt={`${CARD_TYPES.articlePreviewCard}-split-image`}
              />
              {(contentType === 'podcast' || contentType === 'talkshow') && (
                <Label>
                  {contentType === 'podcast' ? <PodcastIcon /> : <PlayIcon />}
                </Label>
              )}
            </ImageContainer>
          )}
          {renderCardCopy()}
        </>
      )}
    </StyledLink>
  );
};

export default ArticlePreviewCard;

const StyledLink = styled(Link)`
  background: ${({ theme }) => theme.articlePreviewCard.background};
  border-radius: ${({ theme }) => theme.radiuses.M};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
  display: flex;
  flex-direction: column;
  max-height: 346px;
  overflow: hidden;

  img {
    transform: scale(1);
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.1);
    transition: transform 0.3s;
  }

  &:active {
    box-shadow: none;
  }

  ${mq.from.breakpoint.M`
    max-height: 360px;
  `}

  ${mq.from.breakpoint.L`
    max-height: 488px;
  `}

  ${mq.from.breakpoint.XL`
    max-height: 636px;
  `}
`;

const FullImageContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;

  &:before {
    background: ${elevations.gradientBottomBlackToTransparent};
    bottom: 0;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 1;
  }
`;

const ImageContainer = styled.div<{ imageHeight?: string }>`
  height: ${({ imageHeight }) => (imageHeight === 'large' ? '233' : '174')}px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  height: 100%;
  object-fit: cover;
  transition: all 0.2s ease-in-out;
  width: 100%;
`;

const TextContainer = styled.div<{ positionAbsolute?: boolean }>`
  color: ${({ theme }) => theme.articlePreviewCard.metaColor};
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  padding: ${({ theme }) =>
    theme.articlePreviewCard.paddingTextContainer.default};

  ${({ positionAbsolute }) =>
    positionAbsolute
      ? css`
          bottom: 0;
          position: absolute;
          z-index: 1;
        `
      : undefined}
`;

const Label = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.abnGreen100};
  bottom: 0;
  color: ${Colors.white};
  display: flex;
  left: 0;
  padding: ${Spacings.XS};
  position: absolute;
  z-index: 1;

  > svg {
    display: none;
  }

  ${mq.from.breakpoint.M`
    padding: ${Spacings.S};

    > svg {
      display: block;
    }
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M} ${Spacings.S};
  `}
`;

const StyledTagList = styled(TagList)`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${Spacings.XS};
  list-style: none;
`;

const Title = styled(H3)<{ colorWhite?: boolean }>`
  hyphens: auto;
  margin-bottom: 0;
  margin-top: 0;
  word-break: break-word;

  ${({ colorWhite }) =>
    colorWhite
      ? css`
          color: ${({ theme }) => theme.colors.abnNeutralWhite};
          font-family: ${({ theme }) => theme.fontBold};
        `
      : css`
          color: ${({ theme }) => theme.articlePreviewCard.titleColor};
        `}
`;
